import React, { useEffect, useRef, useState } from 'react';

const TechArsenal = () => {
  const [visibleCategories, setVisibleCategories] = useState(new Set());
  const sectionRef = useRef(null);

  const skillCategories = [
    {
      id: 'ai-agentic',
      title: 'AI & Agentic Systems',
      description: 'Building intelligent, stateful AI agents with modern frameworks',
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      skills: ['LangGraph', 'LangChain', 'MCP Protocol', 'RAG', 'Claude / Anthropic', 'ReAct Pattern', 'Prompt Engineering', 'Pydantic'],
    },
    {
      id: 'python-data',
      title: 'Python & Data Engineering',
      description: 'Processing terabyte-scale datasets and building robust data pipelines',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      skills: ['Python', 'pandas', 'xarray', 'NetCDF / HDF5 / Zarr', 'ETL Pipelines', 'SQL / PostgreSQL', 'FastAPI', 'Flask / REST APIs', 'asyncio'],
    },
    {
      id: 'cloud-devops',
      title: 'Cloud & Infrastructure',
      description: 'Architecting scalable cloud platforms and containerized deployments',
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
      skills: ['AWS (EC2, ECS, ECR, EFS)', 'Docker', 'Microservices', 'Swagger / OpenAPI', 'JupyterHub', 'Route 53 / ACM', 'GitHub Actions', 'Linux / HPC (SLURM)', 'Bash / Shell', 'SaaS Architecture'],
    },
    {
      id: 'scientific',
      title: 'Scientific Computing & ML',
      description: '9 years of computational science and machine learning applications',
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      skills: ['WRF-Hydro', 'METplus', 'DICast®', 'Calibration / Parameter Estimation', 'scikit-learn', 'Ensemble Methods (Stacking)', 'NLP', 'Fortran / C++', 'GIS / Geospatial'],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.dataset.categoryId;
            setVisibleCategories((prev) => new Set([...prev, id]));
          }
        });
      },
      { threshold: 0.2 }
    );

    const categories = sectionRef.current?.querySelectorAll('.skill-category');
    categories?.forEach((category) => observer.observe(category));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="skills" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-20 w-80 h-80 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-teal-200/20 to-cyan-200/20 rounded-full blur-3xl" />
      </div>

      <div ref={sectionRef} className="max-w-5xl mx-auto relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-sm font-medium mb-4">
            Technical Expertise
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Technical <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Skills</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            A unique blend of scientific computing expertise and cutting-edge AI engineering
          </p>
        </div>

        {/* Skill Categories Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {skillCategories.map((category, categoryIndex) => {
            const isVisible = visibleCategories.has(category.id);

            return (
              <div
                key={category.id}
                data-category-id={category.id}
                className={`skill-category bg-white rounded-2xl border ${category.borderColor} shadow-sm hover:shadow-lg p-6 transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${categoryIndex * 0.1}s` }}
              >
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-r ${category.color} text-white`}>
                    {category.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {category.title}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 mb-5">{category.description}</p>

                {/* Skills as tags */}
                <div className="flex flex-wrap gap-2">
                  {category.skills.map((skill, skillIndex) => (
                    <span
                      key={skill}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium ${category.bgColor} text-gray-700 border ${category.borderColor} transition-all duration-500 ${
                        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                      }`}
                      style={{ transitionDelay: `${(categoryIndex * 0.1) + (skillIndex * 0.05)}s` }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* What Makes This Stack Unique */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            What Makes This Stack <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Unique</span>?
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            My 9 years at NCAR gave me deep expertise in handling
            terabyte-scale atmospheric data, while my pivot to AI equipped me to build
            intelligent agentic systems that make sense of it all.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { label: 'Domain Expertise', color: 'from-blue-500 to-indigo-500' },
              { label: 'Data Engineering', color: 'from-purple-500 to-pink-500' },
              { label: 'AI/ML Engineering', color: 'from-teal-500 to-cyan-500' },
              { label: 'Cloud Architecture', color: 'from-orange-500 to-amber-500' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 text-sm">
                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${item.color}`} />
                <span className="text-gray-600">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechArsenal;
